import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Expand } from "lucide-react";
import { GALLERY_IMAGES } from "../../data/content";
import { FadeUp, MaskLineInView } from "./Reveal";
import { Lightbox } from "./Lightbox";

export const Gallery = () => {
  const railRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const step = Math.min(560, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-[#fbf8f0] py-12 lg:py-16"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <FadeUp>
              <span className="font-body text-[#c56928] text-[13px] tracking-[0.3em] uppercase">
                Gallery
              </span>
            </FadeUp>
            <h2 className="font-display text-[#07412b] text-[52px] sm:text-7xl lg:text-[110px] mt-2 leading-[1]">
              <MaskLineInView>Every corner,</MaskLineInView>
              <MaskLineInView delay={0.08}>a little story.</MaskLineInView>
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              data-testid="gallery-prev"
              aria-label="Previous"
              className="h-11 w-11 rounded-full border border-[#07412b]/20 flex items-center justify-center hover:bg-[#07412b] hover:text-[#ddb128] transition-colors text-[#07412b]"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              data-testid="gallery-next"
              aria-label="Next"
              className="h-11 w-11 rounded-full border border-[#07412b]/20 flex items-center justify-center hover:bg-[#07412b] hover:text-[#ddb128] transition-colors text-[#07412b]"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={railRef}
        className="mt-12 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
        data-lenis-prevent
      >
        <div className="flex gap-4 sm:gap-6 px-5 lg:px-8 max-w-7xl mx-auto pb-2">
          {GALLERY_IMAGES.map((img, i) => (
            <motion.figure
              key={i}
              data-testid={`gallery-item-${i + 1}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.05 }}
              className={`group relative snap-start shrink-0 overflow-hidden rounded-2xl bg-[#07412b]/5 cursor-zoom-in ${
                img.ratio === "portrait"
                  ? "w-[70vw] sm:w-[340px] lg:w-[380px] aspect-[3/4]"
                  : "w-[85vw] sm:w-[460px] lg:w-[560px] aspect-[4/3]"
              }`}
              onClick={() => setActiveIndex(i)}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* zoom affordance */}
              <div className="absolute top-3 right-3 h-9 w-9 rounded-full bg-[#052e1e]/40 backdrop-blur-sm text-[#ddb128] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand size={15} />
              </div>
              <figcaption className="absolute bottom-3 left-4 right-4 font-script text-[#f7f3e9] text-lg drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                {img.alt}
              </figcaption>
            </motion.figure>
          ))}
          {/* trailing spacer for edge padding on mobile */}
          <div className="shrink-0 w-2" aria-hidden="true" />
        </div>
      </div>

      <p className="mt-8 text-center font-body text-[#07412b]/55 text-sm tracking-wide">
        Tap any photo to zoom · swipe or drag to explore →
      </p>

      <Lightbox
        images={GALLERY_IMAGES}
        index={activeIndex}
        onClose={() => setActiveIndex(null)}
        onChange={setActiveIndex}
      />
    </section>
  );
};
